import React, { PureComponent } from 'react';
import { FlowGrid } from './FlowGrid';
import { SortUtils } from 'app/common/util/SortUtils';
import MeasureUtils from 'app/common/util/MeasureUtils';
import { TVLoad } from './TVLoad';
import { Flex } from 'app/components/common/Flex';
import {
    ExtendedMonitoredTv,
    MonitoredEvent,
    MonitoredMcdmMeasure,
    MonitoredMeasure,
    TvMeteoAlert,
} from 'app/model/SOL49';
import { FlightId, HotspotDto, TimePeriod } from 'app/model/CORE';
import { ClosablePopover } from 'app/components/common/ClosablePopover';
import { FlowSettings } from './settings/FlowSettings';
import { SettingFilled } from '@ant-design/icons';
import { SettingsContentPanel } from 'app/components/common/SettingsContentPanel';
import { Button, Checkbox, Select } from 'antd';
import MonitoredLoadUtils from 'app/common/util/MonitoredLoadUtils';
import { MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import Utils from 'app/common/util/Utils';
import { NumberOfFlowColumns } from '../../../../common/context/AppConstants';

export const DEFAULT_CONCERNED_TV_SETTINGS: ConcernedTVSettings = {
    sortBy: 'name',
    tv: 'all',
    showOccupancy: true,
    occupancy: 'all',
    showOccupancyForTv: true,
    occupancyForTv: 'all',
    showLoadInactivePeriods: false,
};

export interface ConcernedTVSettings {
    sortBy: 'name';
    tv: 'all' | 'low' | 'high' | 'overload';
    showOccupancy: boolean;
    occupancy: 'all' | 'sustain' | 'peak';
    showOccupancyForTv: boolean;
    occupancyForTv: 'all' | 'low' | 'high' | 'overload';
    showLoadInactivePeriods: boolean;
}

interface ConcernedTVSectionProps {
    currentTime: Date;
    tvs: Array<ExtendedMonitoredTv>;
    monitoredMeasures: Array<MonitoredMeasure>;
    pendingMeasureProposals?: Array<MonitoredMcdmMeasure>;
    events: MonitoredEvent[];
    tvSetMap?: { [key: string]: string };
    meteoAlerts?: { [key: string]: Array<TvMeteoAlert> };
    hotspots?: { [key: string]: Array<HotspotDto> };
    tvsOccupancyPeriods?: { [key: string]: Map<FlightId, TimePeriod> };
    nbColumns: NumberOfFlowColumns;

    selectedTvs: ExtendedMonitoredTv[];
    onSelectionChange?(tvs: ExtendedMonitoredTv[], selected: boolean);

    settings: ConcernedTVSettings;
    onSettingsChange: (ConcernedTVSettings) => void;
}

interface ConcernedTVSectionState {
    settings: ConcernedTVSettings;
}

export class ConcernedTVSection extends PureComponent<ConcernedTVSectionProps, ConcernedTVSectionState> {
    private settingsPopoverRef = React.createRef<ClosablePopover>();

    state = {
        settings: this.props.settings,
    };

    componentWillUnmount() {
        // Persist the current settings in parent
        this.props.onSettingsChange(this.state.settings);
    }

    handleSelectionChange = (tv: ExtendedMonitoredTv, selected: boolean) => {
        this.props.onSelectionChange?.([tv], selected);
    };

    handleSettingsChange = (settings: FlowSettings) => {
        this.setState({ settings: settings }, () => this.settingsPopoverRef.current.hide());
    };

    getFilteredMonitoredTvList = (monitoredTvList: Array<ExtendedMonitoredTv>, settings: ConcernedTVSettings) => {
        return monitoredTvList?.filter(monitoredTv =>
            MonitoredLoadUtils.isEntryDisplayed(monitoredTv.loadValues, settings.tv),
        );
    };

    render() {
        // Filter data based on display settings
        const filteredMonitoredTvList = this.getFilteredMonitoredTvList(this.props.tvs, this.state.settings)?.sort(
            SortUtils.MONITORED_TV_ALPHABETICAL_SORTING,
        );
        const nbFilteredOutTvs = this.props.tvs?.length - filteredMonitoredTvList?.length;

        return (
            <Flex
                className="tvs-concerned-by-selection overflow"
                direction="vertical"
                grow={false}
                shrink={false}
                style={{ maxHeight: '30%' }}>
                <Flex className="on-hover" style={{ margin: '1em' }}>
                    <Flex>
                        <div style={{ fontWeight: 'bold' }}>
                            {this.props.tvs?.length} TFV{this.props.tvs?.length > 1 ? 's' : ''} concerned by the
                            selection
                            {nbFilteredOutTvs ? ' (' + nbFilteredOutTvs + ' filtered out)' : ''}
                        </div>
                    </Flex>
                    <Flex style={{ justifyContent: 'flex-end' }}>
                        <ClosablePopover
                            key="settings-action"
                            ref={this.settingsPopoverRef}
                            trigger="click"
                            placement="bottom"
                            title={<ConcernedTVSettingsTitle />}
                            content={
                                <ConcernedTVSettingsContent
                                    settings={this.state.settings}
                                    onChange={this.handleSettingsChange}
                                    onClose={() => this.settingsPopoverRef.current.hide()}
                                />
                            }>
                            <SettingFilled
                                style={{ fontSize: '1.2em' }}
                                onClick={() => this.settingsPopoverRef.current.show()}
                            />
                        </ClosablePopover>
                    </Flex>
                </Flex>
                <FlowGrid columns={this.props.nbColumns}>
                    {filteredMonitoredTvList?.map(monitoredTv => {
                        const tvId = monitoredTv.refLocation.id;
                        const tvMeasures = this.props.monitoredMeasures?.filter(
                            monitoredMeasure => tvId === MeasureUtils.getTvLocation(monitoredMeasure.measure).id,
                        );
                        const occupancyPeriods = this.props.tvsOccupancyPeriods?.[tvId];
                        const pendingMeasureProposal = this.props.pendingMeasureProposals?.find(
                            proposal => MeasureUtils.getTvLocation(proposal.measure).id === tvId,
                        );
                        const events = this.props.events?.filter(
                            e =>
                                (MonitoredEventUtils.isWeatherEvent(e) &&
                                    e.refLocs?.some(refLoc =>
                                        Utils.isShallowEquals(refLoc, monitoredTv.tvRefLocation),
                                    )) ||
                                (MonitoredEventUtils.isNoteEvent(e) && e.tvs?.includes(tvId)),
                        );
                        return (
                            <TVLoad
                                key={tvId}
                                tvName={tvId}
                                vsName={this.props.tvSetMap?.[tvId]}
                                elementary={monitoredTv.elementary}
                                entryLoad={monitoredTv.loadValues}
                                occupancyLoad={monitoredTv.occupancyLoadValues}
                                monitoredMeasures={tvMeasures}
                                pendingMeasureProposal={pendingMeasureProposal}
                                activePeriods={monitoredTv.activePeriods}
                                showLoadInactivePeriods={this.state.settings.showLoadInactivePeriods}
                                occupancyPeriods={occupancyPeriods}
                                currentTime={this.props.currentTime}
                                monitorable={monitoredTv.monitorable}
                                selected={this.props.selectedTvs?.indexOf(monitoredTv) >= 0}
                                onSelectionChange={selected => this.handleSelectionChange(monitoredTv, selected)}
                                tvMeteoAlerts={this.props.meteoAlerts?.[tvId]}
                                events={events}
                                hotspots={this.props.hotspots?.[tvId]}
                            />
                        );
                    })}
                </FlowGrid>
            </Flex>
        );
    }
}

export class ConcernedTVSettingsTitle extends PureComponent {
    render() {
        return (
            <Flex direction="vertical">
                <div style={{ fontWeight: 'bold' }}>SECTION SETTINGS</div>
            </Flex>
        );
    }
}

export class ConcernedTVSettingsContent extends SettingsContentPanel<ConcernedTVSettings> {
    render() {
        return (
            <Flex direction="vertical" className="settings-content" style={{ width: '26em', padding: '1em' }}>
                <Flex>
                    <Flex className="label">Sort by</Flex>
                    <Flex size="12em">
                        <Select
                            value={this.state.sortBy}
                            disabled={true}
                            onChange={value => this.setState({ sortBy: value })}
                            style={{ width: '12em' }}
                            // Force render of dropdown inside the popover content
                            // Fix popover hiding on click
                            getPopupContainer={trigger => trigger.parentNode}>
                            <Select.Option value="name">Name</Select.Option>
                        </Select>
                    </Flex>
                </Flex>
                <Flex>
                    <Flex className="label">TVs to show</Flex>
                    <Flex size="12em">
                        <Select
                            value={this.state.tv}
                            onChange={value => this.setState({ tv: value })}
                            style={{ width: '12em' }}
                            getPopupContainer={trigger => trigger.parentNode}>
                            <Select.Option value="all">All</Select.Option>
                            <Select.Option value="low">Low threshold</Select.Option>
                            <Select.Option value="high">High threshold</Select.Option>
                            <Select.Option value="overload">Overload</Select.Option>
                        </Select>
                    </Flex>
                </Flex>
                <Flex>
                    <Flex className="label">
                        <Checkbox
                            checked={this.state.showOccupancy}
                            onChange={value => this.setState({ showOccupancy: value.target.checked })}>
                            Show occupancy
                        </Checkbox>
                    </Flex>
                    <Flex size="12em">
                        <Select
                            value={this.state.occupancy}
                            disabled={!this.state.showOccupancy}
                            onChange={value => this.setState({ occupancy: value })}
                            style={{ width: '12em' }}
                            getPopupContainer={trigger => trigger.parentNode}>
                            <Select.Option value="all">All</Select.Option>
                            <Select.Option value="sustain">Sustain value</Select.Option>
                            <Select.Option value="peak">Peak value</Select.Option>
                        </Select>
                    </Flex>
                </Flex>
                <Flex>
                    <Flex className="label">
                        <Checkbox
                            checked={this.state.showOccupancyForTv}
                            disabled={!this.state.showOccupancy}
                            onChange={value => this.setState({ showOccupancyForTv: value.target.checked })}>
                            Only for TVs
                        </Checkbox>
                    </Flex>
                    <Flex size="12em">
                        <Select
                            value={this.state.occupancyForTv}
                            disabled={!this.state.showOccupancy || !this.state.showOccupancyForTv}
                            onChange={value => this.setState({ occupancyForTv: value })}
                            style={{ width: '12em' }}
                            getPopupContainer={trigger => trigger.parentNode}>
                            <Select.Option value="all">All</Select.Option>
                            <Select.Option value="low">Low threshold</Select.Option>
                            <Select.Option value="high">High threshold</Select.Option>
                            <Select.Option value="overload">Overload</Select.Option>
                        </Select>
                    </Flex>
                </Flex>
                <Flex>
                    <Flex className="label">
                        <Checkbox
                            checked={this.state.showLoadInactivePeriods}
                            onChange={e => this.setState({ showLoadInactivePeriods: e.target.checked })}>
                            Show load for non-active periods
                        </Checkbox>
                    </Flex>
                </Flex>
                <Flex style={{ justifyContent: 'space-around' }}>
                    <Button onClick={this.handleApply}>Apply</Button>
                    <Button onClick={this.handleClose}>Close</Button>
                </Flex>
            </Flex>
        );
    }
}
